/**
 * 배포 화면의 "자정 자동 배포" — 이 현장의 서비스를 고른 버전으로 맞춰 두는 예약.
 *
 * 매일 00:00(KST) 백엔드가 현장 통합서버를 다시 읽어, 체크한 서비스 중 실행 버전이 고른
 * 버전과 다른 것만 사내 이미지를 승격한다(현장 통합서버 배포와 같은 길). 버전을 읽지 못한
 * 서비스는 배포하지 않는다. 결과는 배포 히스토리(실행자 "자동 배포(자정)")와 Teams 한 건.
 *
 * 버전 목록은 사내 서버에 실제로 있는 vX.Y.Z (현장 배포 다이얼로그와 같은 출처), 통합서버
 * 목록도 다이얼로그와 같다. 저장은 관리자만 — 현장 배포 시작과 같은 권한.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Typography,
  alpha,
} from '@mui/material';
import { NightsStay as NightIcon } from '@mui/icons-material';

import siteBatchService from '../../../services/SiteBatchService';
import { SiteNetworkDeviceService } from '../../../services/SiteNetworkDeviceService';
import type { AutoDeployResult } from '../../../models/SiteBatch';
import { getUserInfo } from '../../../utils/auth';
import { integrationServers, serverLabel } from '../../../views/SiteDashboardView/hooks/remoteDeploy';
import { useServiceVersions } from '../../../views/SiteDashboardView/hooks/useServiceVersions';
import { useDashboardTokens } from '../../../views/SiteDashboardView/tokens';
import {
  OUTCOME_META,
  draftFromSetting,
  draftPayload,
  draftProblem,
  versionOptions,
  type AutoDeployDraft,
  type AutoDeployRow,
  type OutcomeTone,
} from './autoDeploy';
import { clock } from './runningVersions';

interface Props {
  projectId: number;
  /** 마지막 실행이 시작한 배치를 진행 모달로 연다. */
  onOpenBatch: (batchId: number) => void;
}

/** 폰에서 확대되지 않게 입력은 16px. */
const INPUT_SX = { fontSize: '1rem', minHeight: 44 } as const;

const AutoDeployCard: React.FC<Props> = ({ projectId, onOpenBatch }) => {
  const { colors, type, space } = useDashboardTokens();
  const queryClient = useQueryClient();
  const writable = getUserInfo()?.role === 'admin';

  const setting = useQuery({
    queryKey: ['auto-deploy', projectId],
    queryFn: () => siteBatchService.getAutoDeploy(projectId),
  });
  // 배포 대상 카드·다이얼로그와 같은 key — 캐시를 같이 쓴다.
  const devices = useQuery({
    queryKey: ['site-network-devices', projectId],
    queryFn: () => SiteNetworkDeviceService.list(projectId),
  });
  const servers = useMemo(() => integrationServers(devices.data ?? []), [devices.data]);

  const initial = useMemo(
    () => (setting.data ? draftFromSetting(setting.data, servers.length === 1 ? servers[0].id : null) : null),
    [setting.data, servers],
  );
  const [draft, setDraft] = useState<AutoDeployDraft | null>(null);
  useEffect(() => setDraft(initial), [initial]);

  const save = useMutation({
    mutationFn: (d: AutoDeployDraft) => siteBatchService.saveAutoDeploy(projectId, draftPayload(d)),
    onSuccess: (saved) => queryClient.setQueryData(['auto-deploy', projectId], saved),
  });
  const saveError = save.error
    ? (axios.isAxiosError(save.error) && save.error.response?.data?.message) || '저장하지 못했습니다. 잠시 후 다시 시도해주세요.'
    : null;

  const tone = (t: OutcomeTone) =>
    ({ success: colors.success, danger: colors.danger, warn: colors.warn, accent: colors.accent, muted: colors.muted })[t];

  const dirty = !!draft && !!initial && JSON.stringify(draftPayload(draft)) !== JSON.stringify(draftPayload(initial));
  const problem = draft ? draftProblem(draft) : null;
  const updateRow = (appname: string, next: Partial<AutoDeployRow>) =>
    setDraft((d) => d && { ...d, rows: d.rows.map((r) => (r.appname === appname ? { ...r, ...next } : r)) });

  const last = setting.data;
  const resultOf = (appname: string) => last?.last_results?.find((r) => r.appname === appname);
  const color = colors.warn; // 현장 통합서버 배포와 같은 색

  return (
    <Box
      component="section"
      aria-labelledby="auto-deploy-heading"
      sx={{
        bgcolor: colors.surface,
        border: `1px solid ${colors.border}`,
        borderLeft: `6px solid ${draft?.enabled ? color : colors.border}`,
        borderRadius: 2,
        p: `${space.md}px`,
        transition: 'border-color 150ms',
      }}
      data-testid="auto-deploy-card"
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', md: 'flex-start' }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: type.caption.size,
              fontWeight: type.caption.weight,
              letterSpacing: type.caption.spacing,
              textTransform: 'uppercase',
              color: colors.muted,
            }}
          >
            Nightly
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <NightIcon sx={{ color }} />
            <Typography
              id="auto-deploy-heading"
              component="h2"
              sx={{ fontSize: type.h2.size, fontWeight: type.h2.weight, letterSpacing: type.h2.spacing, color: colors.text }}
            >
              자정 자동 배포
            </Typography>
          </Stack>
          <Typography sx={{ fontSize: '0.8rem', color: colors.muted, mt: 0.5 }}>
            매일 00:00 현장 통합서버를 다시 읽어, 체크한 서비스 중 버전이 다른 것만 사내 이미지를 올립니다. 현장
            버전을 읽지 못한 서비스는 배포하지 않습니다. 결과는 배포 히스토리와 Teams 로 남습니다.
          </Typography>
        </Box>
        {draft && (
          <FormControlLabel
            control={
              <Switch
                checked={draft.enabled}
                onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
                disabled={!writable || save.isPending}
                inputProps={{ 'aria-label': '자정 자동 배포 켜기' }}
                data-testid="auto-deploy-enabled"
              />
            }
            label={draft.enabled ? '켜짐' : '꺼짐'}
            sx={{ m: 0, minHeight: 44, flexShrink: 0, '& .MuiFormControlLabel-label': { fontWeight: 700 } }}
          />
        )}
      </Stack>

      {setting.isLoading || !draft ? (
        setting.isError ? (
          <Alert severity="error" variant="outlined" sx={{ mt: 1.5 }}>
            자동 배포 설정을 불러오지 못했습니다.
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={22} />
          </Box>
        )
      ) : (
        <>
          <Box sx={{ mt: 1.5 }}>
            <Typography component="label" htmlFor="auto-deploy-server" sx={{ fontSize: '0.8rem', color: colors.muted }}>
              배포할 통합서버
            </Typography>
            {servers.length === 0 && !devices.isLoading ? (
              <Typography sx={{ fontSize: '0.85rem', color: colors.warn }}>
                통합서버 미등록 — 현장 설정의 네트워크 장비에서 먼저 등록하세요.
              </Typography>
            ) : (
              <Select
                id="auto-deploy-server"
                fullWidth
                size="small"
                displayEmpty
                value={draft.targetDeviceId ?? ''}
                onChange={(e) => setDraft({ ...draft, targetDeviceId: Number(e.target.value) || null })}
                disabled={!writable || save.isPending}
                sx={{ ...INPUT_SX, maxWidth: { md: 420 } }}
                data-testid="auto-deploy-server"
              >
                <MenuItem value="" disabled>
                  통합서버 선택
                </MenuItem>
                {servers.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {serverLabel(d)}
                  </MenuItem>
                ))}
              </Select>
            )}
          </Box>

          <Box role="list" aria-label="자동 배포 서비스" sx={{ mt: 1.5, borderTop: `1px solid ${colors.border}` }}>
            {draft.rows.map((r) => (
              <ServiceRow
                key={r.appname}
                projectId={projectId}
                row={r}
                result={resultOf(r.appname)}
                runAt={last?.last_run_at ?? null}
                disabled={!writable || save.isPending}
                tone={tone}
                onChange={(next) => updateRow(r.appname, next)}
              />
            ))}
          </Box>

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', md: 'center' }}
            sx={{ mt: 1.5 }}
          >
            <Typography sx={{ fontSize: '0.8rem', color: colors.muted }} data-testid="auto-deploy-last-run">
              {last?.last_run_at ? `마지막 실행 ${clock(last.last_run_at)}` : '아직 실행한 적 없음'}
              {last?.updated_by ? ` · 설정 ${last.updated_by}` : ''}
              {last?.last_batch_id ? (
                <>
                  {' · '}
                  <Button
                    size="small"
                    onClick={() => onOpenBatch(last.last_batch_id as number)}
                    sx={{ minHeight: 44, textTransform: 'none', p: 0, minWidth: 0, verticalAlign: 'baseline' }}
                    data-testid="auto-deploy-batch-link"
                  >
                    배치 #{last.last_batch_id} 보기
                  </Button>
                </>
              ) : null}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
              {writable && (problem || !dirty) && (
                <Typography sx={{ fontSize: '0.8rem', color: problem ? colors.warn : colors.muted }}>
                  {problem ?? (save.isSuccess ? '저장했습니다' : '')}
                </Typography>
              )}
              {!writable && <Typography sx={{ fontSize: '0.8rem', color: colors.muted }}>저장은 관리자만</Typography>}
              <Button
                variant="contained"
                disableElevation
                onClick={() => save.mutate(draft)}
                disabled={!writable || !dirty || !!problem || save.isPending}
                sx={{
                  minHeight: 44,
                  px: 3,
                  flexShrink: 0,
                  textTransform: 'none',
                  fontWeight: 700,
                  background: color,
                  color: '#1a1200',
                  '&:hover': { background: color, boxShadow: `0 6px 16px ${alpha(color, 0.3)}` },
                  '&.Mui-disabled': { background: (t) => t.palette.action.disabledBackground },
                  '&:focus-visible': { outline: `2px solid ${color}`, outlineOffset: 2 },
                }}
                data-testid="auto-deploy-save"
              >
                {save.isPending ? '저장 중…' : '저장'}
              </Button>
            </Stack>
          </Stack>
          {saveError && (
            <Alert severity="error" sx={{ mt: 1 }} onClose={() => save.reset()}>
              {saveError}
            </Alert>
          )}
        </>
      )}
    </Box>
  );
};

interface ServiceRowProps {
  projectId: number;
  row: AutoDeployRow;
  result: AutoDeployResult | undefined;
  runAt: string | null;
  disabled: boolean;
  tone: (t: OutcomeTone) => string;
  onChange: (next: Partial<AutoDeployRow>) => void;
}

/** 서비스 한 줄 — 체크 · 사내 버전 · 마지막 실행 결과. 버전 목록은 체크한 행만 불러온다. */
const ServiceRow: React.FC<ServiceRowProps> = ({ projectId, row, result, runAt, disabled, tone, onChange }) => {
  const { colors, type, space } = useDashboardTokens();
  const versions = useServiceVersions(String(projectId), row.appname, { enabled: row.checked, source: 'company' });
  const options = versionOptions(versions.data?.map((v) => v.name), row.version);
  const meta = result ? OUTCOME_META[result.outcome] : null;

  return (
    <Box
      role="listitem"
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(180px, 1fr) minmax(160px, 220px) minmax(0, 2fr)' },
        alignItems: 'center',
        columnGap: 2,
        rowGap: 0.5,
        py: `${space.sm}px`,
        borderBottom: `1px solid ${colors.border}`,
      }}
      data-testid={`auto-deploy-row-${row.appname}`}
    >
      <FormControlLabel
        control={
          <Checkbox
            checked={row.checked}
            onChange={(e) => onChange({ checked: e.target.checked })}
            disabled={disabled}
            inputProps={{ 'aria-label': `${row.label} 자동 배포` }}
          />
        }
        label={
          <Box component="span" sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <Typography component="span" sx={{ fontWeight: 600, fontSize: '0.9rem', color: colors.text }}>
              {row.label}
            </Typography>
            <Typography component="span" sx={{ fontFamily: type.mono.family, fontSize: '0.7rem', color: colors.muted }}>
              {row.appname}
            </Typography>
          </Box>
        }
        sx={{ m: 0, minHeight: 44, minWidth: 0 }}
      />
      <Select
        size="small"
        fullWidth
        displayEmpty
        value={row.version}
        onChange={(e) => onChange({ version: String(e.target.value) })}
        disabled={disabled || !row.checked}
        sx={{ ...INPUT_SX, fontFamily: type.mono.family }}
        inputProps={{ 'aria-label': `${row.label} 버전` }}
        data-testid={`auto-deploy-version-${row.appname}`}
      >
        <MenuItem value="" disabled>
          {!row.checked ? '—' : versions.isLoading ? '사내 버전 불러오는 중…' : versions.isError ? '사내 버전을 못 읽음' : '버전 선택'}
        </MenuItem>
        {options.map((o) => (
          <MenuItem key={o.value} value={o.value}>
            {o.missing ? `${o.value} (사내에 없음)` : o.value}
          </MenuItem>
        ))}
      </Select>
      <Typography sx={{ fontSize: '0.75rem', color: colors.muted, minWidth: 0, overflowWrap: 'anywhere' }}>
        {meta && result ? (
          <>
            <Box component="span" sx={{ fontWeight: 700, color: tone(meta.tone) }}>
              {meta.label}
            </Box>
            {` · ${clock(runAt)} · ${result.running_version || '?'} → ${result.target_version}`}
            {result.reason ? ` · ${result.reason}` : ''}
          </>
        ) : row.checked ? (
          '아직 실행 결과 없음'
        ) : (
          ''
        )}
      </Typography>
    </Box>
  );
};

export default AutoDeployCard;
